import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import {
  persistentReducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import type { LoadedState } from '@ardoq/api-types';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import { aiControlledCommandApplied } from 'aiControlledCommands/aiControlledCommandApplied';
import { loadedState$ } from 'loadedState/loadedState$';
import {
  openedViewpointData$,
  type OpenedViewpointData,
} from 'appLayout/ardoqStudio/saveAsViewpoint/saveAsViewpoint$/openedViewpointData$';

export type ViewChangingCommandId =
  | AiControlledCommandId.APPLY_CONDITIONAL_FORMATTING
  | AiControlledCommandId.APPLY_LABEL_FORMATTING
  | AiControlledCommandId.CHANGE_VIEW_SETTINGS
  | AiControlledCommandId.CHANGE_TRAVERSAL_CONTEXT
  | AiControlledCommandId.LAYOUT_BLOCKS;

const VIEW_CHANGING_COMMANDS: ReadonlySet<AiControlledCommandId> = new Set([
  AiControlledCommandId.APPLY_CONDITIONAL_FORMATTING,
  AiControlledCommandId.APPLY_LABEL_FORMATTING,
  AiControlledCommandId.CHANGE_VIEW_SETTINGS,
  AiControlledCommandId.CHANGE_TRAVERSAL_CONTEXT,
  AiControlledCommandId.LAYOUT_BLOCKS,
] satisfies ViewChangingCommandId[]);

const isViewChangingCommandId = (
  commandId: AiControlledCommandId
): commandId is ViewChangingCommandId => VIEW_CHANGING_COMMANDS.has(commandId);

export type AiDraftChanges = {
  draftKey: string;
  changes: ViewChangingCommandId[];
};

const toDraftKey = (
  loadedStates: LoadedState[],
  { openedViewpoint }: OpenedViewpointData
): string => {
  const loadedStateUuids = loadedStates.map(({ uuid }) => uuid).join(',');
  const savedVersion = openedViewpoint
    ? `${openedViewpoint._id}:${openedViewpoint.lastUpdated}`
    : 'unsaved';
  return `${loadedStateUuids}|${savedVersion}`;
};

const draftKey$ = combineLatest([loadedState$, openedViewpointData$]).pipe(
  map(([{ loadedStates }, openedViewpointData]) =>
    toDraftKey(loadedStates, openedViewpointData)
  ),
  distinctUntilChanged()
);

const addChange = (
  state: AiDraftChanges,
  commandId: AiControlledCommandId
): AiDraftChanges => {
  if (!isViewChangingCommandId(commandId)) {
    return state;
  }
  return { ...state, changes: [...state.changes, commandId] };
};

const resetOnDraftChange = (
  state: AiDraftChanges,
  draftKey: string
): AiDraftChanges => {
  if (state.draftKey === draftKey) {
    return state;
  }
  return { draftKey, changes: [] };
};

export const aiDraftChanges$ = persistentReducedStream<AiDraftChanges>(
  'aiDraftChanges$',
  { draftKey: '', changes: [] },
  [
    reducer(aiControlledCommandApplied, addChange),
    streamReducer(draftKey$, resetOnDraftChange),
  ]
);
